<template>
    <div class="mb-4">
        <b-row>
            <b-col md="5">
                <b-card class="mb-4">
                    <b-row>
                        <b-col md="6">
                            <b-form-text>Min. Frequency (Hz)</b-form-text>
                            <b-form-input type="number" step="any" v-model="input.minFrequency"></b-form-input>
                        </b-col>
                        <b-col md="6">
                            <b-form-text>Max. Frequency (Hz)</b-form-text>
                            <b-form-input type="number" step="any" v-model="input.maxFrequency"></b-form-input>
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col md="12" style="text-align: end">
                            <b-btn variant="primary" @click="didTapCalculate" :disabled="isSubmitting || !calculateComplete">{{ isSubmitting ? "Calculating..." : "Calculate" }}</b-btn>
                        </b-col>
                    </b-row>
                </b-card>
                <b-card class="mb-4">
                    <b-row>
                        <b-col md="12">
                            <b-form-group label-cols-sm="5" label="Selected Pump" style="margin-bottom: 0">
                                <b-form-select v-model="input.pump" :options="input.optPump" @change="didChangePump">
                                    <template slot="first">
                                        <option :value="null" disabled>{{ headPump }}</option>
                                    </template>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <b-form-text>Available pumps are based on minimum and maximum frequency set above, and set Qdes (<strong>{{ qDes }} BPD</strong>).</b-form-text>
                    <b-row class="mt-4">
                        <b-col md="6">
                            <b-form-text>Solve for</b-form-text>
                            <b-form-select v-model="input.solveFor" :options="optSolveFor" @change="didChangeSolveFor"></b-form-select>
                        </b-col>
                        <b-col md="6">
                            <b-form-text>{{ input.solveFor == "# Stages" ? "Target Frequency" : "# Stages" }}</b-form-text>
                            <b-form-input type="number" step="any" v-model="input.toSolve"></b-form-input>
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col md="12" style="text-align: end">
                            <b-btn variant="primary" :disabled="isSubmitting" @click="didTapSolve">Solve</b-btn>
                        </b-col>
                    </b-row>
                </b-card>
                <b-card>
                    <b-row v-if="input.solveFor == '# Stages'">
                        <b-col md="6">
                            <b-form-text># Stages Required</b-form-text>
                            <b-form-input v-model="input.stagesRequired" readonly style="font-weight: bold"></b-form-input>
                        </b-col>
                        <b-col md="6">
                            <b-form-text># Stages Selected</b-form-text>
                            <b-form-input type="number" step="any" v-model="input.stagesSelected"></b-form-input>
                        </b-col>
                        <b-col md="12" class="mt-2" style="text-align: end">
                            <b-btn variant="primary" :disabled="isSubmitting" @click="didTapSave">Save & Draw Graph</b-btn>
                        </b-col>
                    </b-row>
                    <b-row v-else>
                        <b-col md="6">
                            <b-form-text>Target Frequency</b-form-text>
                            <b-form-input type="number" step="any" v-model="input.targetFrequency" readonly style="font-weight: bold"></b-form-input>
                        </b-col>
                        <b-col md="12" class="mt-2" style="text-align: end">
                            <b-btn variant="primary" :disabled="isSubmitting" @click="didTapSave">Save & Draw Graph</b-btn>
                        </b-col>
                    </b-row>
                </b-card>
            </b-col>
            <b-col v-if="isGraphComplete" md="7">
                <b-card>
                    <div class="mb-2" style="text-align: center">
                        <h4 class="strong">Pump Performance Curve<br />{{ graph.pump }} @{{ graph.frequency }}Hz, {{ graph.stages }} Stages</h4>
                    </div>
                    <scatter-chart :chartData="chartData" :options="chartOptions"></scatter-chart>
                    <hr />
                    <div class="graphInfo">
                        <div class="graphItem">
                            Qdes<br />
                            <h4 class="strong" style="margin-bottom: 0">{{ graph.qDes }}</h4>
                        </div>
                        <div class="graphItem">
                            Head
                            <h4 class="strong" style="margin-bottom: 0; color: #757dd9">{{ graph.head }}</h4>
                        </div>
                        <div class="graphItem">
                            HP
                            <h4 class="strong" style="margin-bottom: 0; color: #f87979">{{ graph.hp }}</h4>
                        </div>
                        <div class="graphItem">
                            Eff
                            <h4 class="strong" style="margin-bottom: 0; color: #388e3c">{{ graph.eff }}%</h4>
                        </div>
                    </div>
                    <div class="mt-4" style="text-align: right">
                        <b-btn variant="primary" v-b-modal.modal-chart>
                            Attach Chart&ensp;<font-awesome-icon icon="file-download" />
                        </b-btn>
                    </div>
                </b-card>
            </b-col>
            <b-modal size="xl" id="modal-chart" title="Attaching Chart to Summary"
                ok-title="Attach"
                :ok-disabled="isAttaching"
                :cancel-disabled="isAttaching"
                :no-close-on-esc="isAttaching"
                :hide-header-close="isAttaching"
                no-close-on-backdrop
                @ok="attachPumpChart">
                <div style="text-align: center">
                    <scatter-chart chart-id="finalChart" :chartData="chartData" :options="chartOptions" style="height: 300px !important; width: 611px !important"></scatter-chart>
                </div>
                <br />
                The chart image above will be attached into your summary report.
            </b-modal>
        </b-row>
    </div>
</template>

<script>
import ScatterChart from '../components/ScatterChart'
import { setTimeout } from 'timers';

export default {
    components: {
        ScatterChart
    },
    name: "pump-selection",
    data() {
        return {
            qDes: 0,

            input: {
                minFrequency: null,
                maxFrequency: null,
                solveFor: "# Stages",
                toSolve: null,
                pump: null,
                optPump: [],
                stagesSelected: null,
                stagesRequired: null,
                targetFrequency: null
            },

            headPump: "Select a pump",
            optSolveFor: ["# Stages", "Target Frequency"],

            isCalculating: false,
            isAttaching: false,
            isSubmitting: false,
            isHeadAchieved: false,

            // Chart data
            chartData: {},
            chartOptions: {},
            graph: {
                head: 0,
                hp: 0,
                eff: 0,
                qDes: 0,
                pump: "",
                frequency: 0,
                stages: 0
            },
            highestY: 0,
            isGraphComplete: false

        }
    },
    computed: {
        calculateComplete() {
            return this.input.minFrequency != "" && this.input.maxFrequency != ""
        }
    },
    watch: {
        highestY(newVal, oldVal) {
            if(newVal != oldVal) {
                this.setupChartData()
            }
        }
    },
    methods: {

        attachPumpChart(modal) {
            modal.preventDefault();

            let c = document.getElementById('finalChart')
            var ctx = c.getContext("2d")
            ctx.globalCompositeOperation = 'destination-over'
            ctx.fillStyle = "white"
            ctx.fillRect(0, 0, 611, 300)

            let calcName = this.$core.general.name

            var that = this
            that.isAttaching = true
            setTimeout(function() {

                let canvas = c.toDataURL('image/jpeg', 0.5)

                let payload = {
                    id: that.$core.id,
                    base64: canvas
                }

                that.$axios.post("/calc/attachPump", payload).then(response => {

                    let res = response.data
                    if(res.message == "OK") {
                        that.$core.attachment.pump = true
                        that.$helper.toastSuccess(that, "Chart successfully attached.")
                        that.$bvModal.hide('modal-chart')
                    }
                    else {
                        that.$helper.toastError(that, res.message)
                    }

                })
                .catch(error => {
                    that.$helper.toastError(that, error)
                })
                .finally(() => {
                    that.isAttaching = false
                })

            }, 1000)
            
        },

        didTapCalculate() {

            let payload = {
                minFreq: this.input.minFrequency,
                maxFreq: this.input.maxFrequency,
                qDes: this.qDes
            }

            this.isSubmitting = true
            this.$axios.post("/pumps/getPumpCandidates", payload).then(response => {

                let res = response.data

                if(res.message == "OK") {
                    
                    var pumps = res.data.pumps

                    // Filters out TG pumps if ID constraint does not meet the requirement
                    if(this.$core.idConstraint < 5.92) {
                        pumps = pumps.filter(pump => !pump.text.startsWith("TG"))
                    }

                    this.input.optPump = pumps

                    if(pumps.count == 0) {
                        this.headPump = "No pumps available."
                    }
                    else {
                        this.headPump = "Select a pump"
                    }

                }
                else {
                    this.$helper.toastError(this, res.message)
                }
                
            })
            .catch(error => {
                this.$helper.toastError(this, "Failed to retrieve pump candidates, please wait a moment and retry!")
            })
            .finally(() => {
                this.isSubmitting = false
            })

        },

        didChangePump() {
        
        },

        didChangeSolveFor() {
            this.input.toSolve = null
            this.input.stagesSelected = null
            this.input.stagesRequired = null
            this.input.targetFrequency = null
        },

        didTapSolve() {

            let payload = {
                pumpId: this.input.pump,
                qDes: this.qDes,
                tdh: this.$core.output.TDH2,
                solveFor: this.input.solveFor,
                targetFrequency: this.input.toSolve,
                stagesSelected: this.input.toSolve
            }

            this.isSubmitting = true
            this.$axios.post("/pumps/solve", payload).then(response => {

                let res = response.data
                if(res.message == "OK") {

                    this.isHeadAchieved = true

                    if(payload.solveFor == "# Stages") {
                        this.input.stagesRequired = res.data.stagesRequired
                    }
                    else {
                        this.input.targetFrequency = res.data.targetFrequency
                    }

                }
                else {

                    if(res.message.startsWith("Head is")) { // Head not achieved yet. Disable save & draw graph
                        this.isHeadAchieved = false
                    }

                    this.$helper.toastError(this, res.message)

                }

            })
            .catch(error => {
                this.$helper.toastError(this, "An error occurred, please retry!")
            })
            .finally(() => {
                this.isSubmitting = false
            })

        },

        didTapSave() {

            // Check if head is achieved
            if(!this.isHeadAchieved) {
                return this.$helper.toastError(this, "Head has not been achieved yet (more stages required), graph cannot be drawn!")
            }

            // Warn users that changing pump variant from TD to TG will reset the equipment section
            let eq = this.$core.equipment
            let shouldReset = false

            if("graph" in this.$core.pump && eq != null && "motor1" in eq) {

                let lastPumpVariant = this.$core.pump.graph.graph.pump.substring(0, 2)
                let currentPumpVariant = this.getSelectedPumpVariant(this.input.pump)

                if(lastPumpVariant == "TD" && currentPumpVariant == "TG") {

                    shouldReset = confirm("Due to various incompatibilities of equipments when upgrading from TD to TG pumps, the 'Equipment Selection' section will be reset.\n\nDo you wish to continue?")
                    if(!shouldReset) { return }

                }

            }

            let additionals = {
                qDes: this.qDes,
                tdh: this.$core.output.TDH2,
                unit: this.$core.general.unit
            }

            let payload = { ...this.input, ...additionals }
            payload.calculationId = this.$core.id
            payload.shouldReset = shouldReset

            if(this.input.solveFor == "Target Frequency") {
                
                if(this.input.targetFrequency == null) { return }

                payload.targetFrequency = this.input.targetFrequency
                payload.stagesSelected = this.input.toSolve

            }
            else {

                if(this.input.stagesSelected == null || this.input.stagesSelected == "") { return }
                payload.targetFrequency = this.input.toSolve

            }

            this.isSubmitting = true
            this.$axios.post("/pumps/drawGraph", payload).then(response => {
                
                let res = response.data

                if(res.message == "OK") {
                    this.graph = res.data.graph
                    this.$core.pump.graph = res.data

                    this.isGraphComplete = true
                    this.refreshChart()

                    if(shouldReset) {
                        this.$core.equipment = {}
                    }

                    this.$router.app.$emit("enableTab", 4)
                }
                else {
                    this.$helper.toastError(this, res.message)
                }

            })
            .catch(() => {
                this.$helper.toastError(this, "Failed to save and retrieve the graph, please wait a moment and retry!")
            })
            .finally(() => {
                this.isSubmitting = false
            })

        },

        refreshChart() {
            this.setupChartOptions()
            this.setupChartData()
        },

        setupChartOptions() {

            let unit = this.$core.general.unit
            let that = this

            this.chartOptions = {
                maintainAspectRatio: false,
                events: [],
                scales: {
                    xAxes: [
                        {
                            scaleLabel: {
                                display: true,
                                labelString: 'Flow Rate (BPD)'
                            },
                            ticks: {
                                beginAtZero: true
                            }
                        }
                    ],
                    yAxes: [
                        {
                            id: 'head',
                            position: 'left',
                            scaleLabel: {
                                display: true,
                                fontColor: '#757dd9',
                                labelString: `Head (${unit})`
                            },
                            ticks: {
                                fontColor: '#757dd9',
                                beginAtZero: true,
                                callback: function(value, index, values) {
                                    if(index == 0) {
                                        that.highestY = value
                                    }
                                    return value
                                }
                            }
                        },
                        {
                            id: 'hp',
                            position: 'left',
                            scaleLabel: {
                                display: true,
                                fontColor: '#f87979',
                                labelString: 'HP'
                            },
                            ticks: {
                                fontColor: '#f87979',
                                beginAtZero: true
                            },
                            gridLines: {
                                display: true,
                                drawTicks: true,
                                drawOnChartArea: false
                            }  
                        },
                        {
                            id: 'eff',
                            position: 'right',
                            scaleLabel: {
                                display: true,
                                fontColor: '#388e3c',
                                labelString: 'Eff'
                            },
                            ticks: {
                                fontColor: '#388e3c',
                                beginAtZero: true
                            },
                            gridLines: {
                                display: true,
                                drawTicks: true,
                                drawOnChartArea: false
                            }  
                        }
                    ]
                },
                legend: {
                    display: true,
                    labels: {
                        filter: function(item, chart) {
                            return !item.text.includes('UNUSED')
                        }
                    }
                },
                title: {
                    display: false
                }
            }

        },

        setupChartData() {
            
            // Data prep
            let data = this.$core.pump.graph
            if(data == null) { return }

            this.chartData = {
                datasets: [
                    { // Head
                        backgroundColor: '#757dd9', borderColor: '#757dd9',
                        lineTension: 0.4, borderWidth: 2, pointRadius: 0,
                        showLine: true, fill: false, yAxisID: 'head', label: 'Head',
                        data: data.head
                    },
                    { // HP
                        backgroundColor: '#f87979', borderColor: '#f87979',
                        lineTension: 0.4, borderWidth: 2, pointRadius: 0,
                        showLine: true, fill: false, yAxisID: 'hp', label: 'HP',
                        data: data.hp
                    },
                    { // Eff
                        backgroundColor: '#388e3c', borderColor: '#388e3c',
                        lineTension: 0.4, borderWidth: 2, pointRadius: 0,
                        showLine: true, fill: false, yAxisID: 'eff', label: 'Eff',
                        data: data.eff
                    },
                    { // Head aux line
                        backgroundColor: '#757dd9', borderColor: '#757dd9',
                        lineTension: 0, borderWidth: 1, pointRadius: 2, borderDash: [5, 5],
                        showLine: true, fill: false, yAxisID: 'head', label: 'UNUSED',
                        data: [
                            // { x: this.qDes, y: 0 },
                            { x: data.graph.qDes, y: data.graph.head },
                            // { x: 0, y: that.headPoint }
                        ]
                    },
                    { // HP aux line
                        backgroundColor: '#f87979', borderColor: '#f87979',
                        lineTension: 0, borderWidth: 1, pointRadius: 2, borderDash: [5, 5],
                        showLine: true, fill: false, yAxisID: 'hp', label: 'UNUSED',
                        data: [
                            // { x: this.qDes, y: 0 },
                            { x: data.graph.qDes, y: data.graph.hp },
                            // { x: 0, y: that.hpPoint }
                        ]
                    },
                    { // Eff aux line
                        backgroundColor: '#388e3c', borderColor: '#388e3c',
                        lineTension: 0, borderWidth: 1, pointRadius: 2, borderDash: [5, 5],
                        showLine: true, fill: false, yAxisID: 'eff', label: 'UNUSED',
                        data: [
                            // { x: this.qDes, y: 0 },
                            { x: data.graph.qDes, y: data.graph.eff },
                            // { x: 0, y: that.eff }
                        ]
                    },
                    { // Optimum Area
                        backgroundColor: 'rgba(255, 249, 196, 0.5)', borderColor: 'rgba(255, 249, 196, 0.5)',
                        lineTension: 0.4, borderWidth: 2, pointRadius: 0,
                        showLine: true, fill: 'start', yAxisID: 'head', label: 'UNUSED',
                        data: [
                            { x: data.optimumArea.x1, y: this.highestY },
                            { x: data.optimumArea.x2, y: this.highestY }
                        ]
                    },
                    { // Qdes line
                        backgroundColor: '#000000', borderColor: '#000000',
                        lineTension: 0, borderWidth: 1, pointRadius: 0, borderDash: [5, 5],
                        showLine: true, fill: false, yAxisID: 'head', label: 'UNUSED',
                        data: [
                            { x: data.graph.qDes, y: 0 },
                            { x: data.graph.qDes, y: this.highestY }
                        ]
                    }
                ]
            }
        },

        getSelectedPumpVariant(pumpId) {

            for(let pump of this.input.optPump) {
                if(pump.value == pumpId) { return pump.text.substring(0, 2) }
            }

            return ""

        }

    },
    created: function() {

        if(this.$core.pump.input != null) {

            this.input = this.$core.pump.input

            // Removes TG opt pumps, if constraint did not fulfill the requirement
            if(this.$core.idConstraint < 5.92) {
                this.input.optPump = this.input.optPump.filter(pump => !pump.text.startsWith("TG"))
            }

        }

        if(this.$core.pump.graph != null) {
            this.isGraphComplete = true
            this.isHeadAchieved = true
            this.graph = this.$core.pump.graph.graph
        }
        
        this.qDes = this.$core.input.qDes
        this.refreshChart()

    },
    beforeRouteLeave(to, from, next) {

        this.$core.pump.input = this.input
        next()

    }
}
</script>

<style scoped>
    .graphInfo {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: space-around;
        align-items: stretch;
        align-content: center;
    }

    .graphItem {
        flex: 1 1 0;
        text-align: center;
    }
</style>